import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { GenericModal } from 'components/Modals'
import { WppTypography } from '@wppopen/components-library-react'
import TableMetaDataRow from 'components/table/TableMetaDataRow'
import Table from 'components/table'
import { createCsApiUrlFromEndpoint } from 'utils'
import useApi, { type ApiResponse } from 'hooks/useApi'
import { columnStructure, type VariantDataType } from './utils'
import type { RowClickedEvent } from 'ag-grid-community'
import type { ToastDetails } from 'components/Banner/BannerTypesUtils'
import type { FEApiError } from 'utils/errorHandling'
import type { ProductsDetailDataV5 } from 'utils/utils.d'
import styles from './VariantData.module.scss'

interface VariantDataProps {
  productAPI: ApiResponse<ProductsDetailDataV5 | null>
  setToastDetails: Dispatch<SetStateAction<ToastDetails | undefined>>
}

export default function VariantData({ productAPI, setToastDetails }: VariantDataProps) {
  const navigate = useNavigate()
  const [isDeleteActive, setIsDeleteActive] = useState(false)
  const [variantId, setVariantId] = useState<string>()
  const [isMasterActive, setIsMasterActive] = useState(false)
  const {
    fetchData: fetchSingleProduct,
    data: singleProductData,
    url: singleProductUrl,
    isLoading: isSingleProductLoading,
  } = productAPI
  const InitialVariantUrl = useMemo(() => createCsApiUrlFromEndpoint(`products/variants/${variantId}`), [variantId])

  const {
    fetchData: deleteVariantFetch,
    url: deleteVariantUrl,
    isLoading: isDeleteVariantLoading,
    setUrl: setDeleteVariantUrl,
  } = useApi<ProductsDetailDataV5>(InitialVariantUrl)

  const {
    fetchData: updateVariant,
    url: updateVariantUrl,
    isLoading: isUpdateVariantLoading,
    setUrl: setUpdateVariantUrl,
  } = useApi<ProductsDetailDataV5>(InitialVariantUrl)

  const variants = singleProductData?.variants || []

  useEffect(() => {
    setDeleteVariantUrl(InitialVariantUrl)
    setUpdateVariantUrl(InitialVariantUrl)
  }, [InitialVariantUrl])

  const handleRefreshData = () => {
    fetchSingleProduct(singleProductUrl).catch((error: Error) => console.error(error))
  }

  const handleOpenDeleteModal = ({ _id }: VariantDataType) => {
    setIsDeleteActive(true)
    setVariantId(_id)
  }

  const handleCloseDeleteModal = () => {
    setIsDeleteActive(false)
    setVariantId(undefined)
  }

  const handleOpenMasterModal = ({ _id }: VariantDataType) => {
    setIsMasterActive(true)
    setVariantId(_id)
  }

  const handleCloseMasterModal = () => {
    setIsMasterActive(false)
    setVariantId(undefined)
  }

  const handleDeleteVariant = async () => {
    if (!variantId) return

    try {
      await deleteVariantFetch(deleteVariantUrl, { method: 'DELETE' })
      handleRefreshData()
      handleCloseDeleteModal()
      setToastDetails({
        message: 'Variant deleted successfully',
        type: 'success',
      })
    } catch (err) {
      const error = err as FEApiError
      const errMessage = error.errors?.[0].message || error.message || 'Something went wrong'
      throw new Error(errMessage)
    }
  }

  const handleSetMasterVariant = async () => {
    if (!variantId) return

    try {
      await updateVariant(updateVariantUrl, { method: 'PATCH', body: JSON.stringify({ master: true }) })
      handleRefreshData()
      handleCloseMasterModal()
    } catch (err) {
      const error = err as FEApiError
      const errMessage = error.errors?.[0].message || error.message || 'Something went wrong'
      throw new Error(errMessage)
    }
  }

  const handleRowClicked = (e: RowClickedEvent) => {
    const target = e.event?.target as HTMLElement
    if (target.tagName.match(/WPP-(ICON-MORE|ACTION-BUTTON|ICON-BUTTON)/)) return
    const data = e.data
    navigate(`variant/${data._id}`)
  }

  return (
    <div className={styles.container} data-testid="variants-data">
      {!variants?.length ? (
        <WppTypography tag="h3" type="s-strong" data-testid="empty-standard-content" className={styles.empty}>
          There are no variants for the product
        </WppTypography>
      ) : (
        <>
          <TableMetaDataRow
            text={`Showing ${variants.length || 0} results`}
            refreshHandler={handleRefreshData}
            isInitialising={false}
          />

          <Table
            theme="tertiary"
            columnDefs={columnStructure(handleOpenDeleteModal, handleOpenMasterModal)}
            loadedRows={variants.length}
            totalRows={variants.length}
            rowData={variants}
            loading={isSingleProductLoading}
            isDrillable
            onRowClicked={handleRowClicked}
            noRowsOverlayComponent={() => (
              <WppTypography tag="p" type="s-body">
                You haven't uploaded any data yet
              </WppTypography>
            )}
          />
        </>
      )}

      {isDeleteActive && (
        <GenericModal
          onSubmit={handleDeleteVariant}
          onCancel={handleCloseDeleteModal}
          modalTitle="Delete variant"
          submitButtonText="Delete"
          isApiLoading={isDeleteVariantLoading}
          shouldModalOpen={isDeleteActive}
        >
          <WppTypography tag="p" type="s-body" data-testid="modal-disclaimer">
            Are you sure you want to delete this variant? This will remove it from your project.
          </WppTypography>
        </GenericModal>
      )}

      {isMasterActive && (
        <GenericModal
          onSubmit={handleSetMasterVariant}
          onCancel={handleCloseMasterModal}
          modalTitle="Make master variant"
          submitButtonText="Save"
          isApiLoading={isUpdateVariantLoading}
          shouldModalOpen={isMasterActive}
        >
          <WppTypography tag="p" type="s-body" data-testid="modal-disclaimer">
            Are you sure you want to make this variant the master? This will make this the variant used for Standard
            Content.
          </WppTypography>
        </GenericModal>
      )}
    </div>
  )
}
